import React, { useState } from 'react';
import { Sparkles, X, ChevronDown } from 'lucide-react';
import { AssistantChat, NAVY, GREEN } from '@/components/AssistantChat';
import { useFeatureGates } from '@/hooks/useFeatureGates';

/**
 * AssistantWidget — global floating launcher + docked chat panel.
 * Runs the live AI assistant (Claude Sonnet 4.6 + Whisper) whenever a user is signed in.
 * Gated behind the `ask_drawready_assistant` feature gate (owner-only until rolled out).
 */
export default function AssistantWidget({ user = null }) {
  const [open, setOpen] = useState(false);
  const live = !!(user && user.id);
  const gates = useFeatureGates(user);

  // Hidden until gates resolve, and hidden entirely for users without access.
  if (!gates || !gates.ask_drawready_assistant) return null;

  const smartReceipt = !!gates.assistant_smart_receipt;

  return (
    <div className="fixed bottom-5 right-5 z-[60]" data-testid="assistant-widget">
      {open && (
        <div className="mb-3 w-[380px] max-w-[calc(100vw-2.5rem)] h-[600px] max-h-[calc(100vh-6rem)] rounded-3xl bg-slate-50 shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4">
          {/* Panel header */}
          <div className="px-4 py-3 flex items-center gap-2.5 text-white" style={{ background: NAVY }}>
            <div className="w-8 h-8 rounded-xl bg-white/15 flex items-center justify-center">
              <Sparkles className="w-4.5 h-4.5" />
            </div>
            <div className="leading-tight">
              <p className="text-sm font-bold">DrawReady Assistant</p>
              <p className="text-[11px] text-white/70 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: GREEN }} /> Powered by Claude
              </p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-widget-close" className="ml-auto w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center">
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
          <AssistantChat heightClass="flex-1" showExamples showFootnote={false} live={live} userId={user?.id} smartReceipt={smartReceipt} />
        </div>
      )}

      {/* Launcher button */}
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-widget-launcher"
        className="ml-auto flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-full text-white shadow-xl hover:shadow-2xl hover:scale-[1.03] active:scale-95 transition-all"
        style={{ background: NAVY }}
      >
        {open ? <X className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
        <span className="text-sm font-bold">Ask DrawReady</span>
      </button>
    </div>
  );
}
